// Notification sound — played alongside every OS banner the notify policy
// decides to fire (channel message, DM). Ported from the original web app's
// notify module: a single <audio> element, volume 0.5, with an "unlock" step
// because browsers (and Chromium under Electron) block audio.play() until the
// page has seen a user interaction.
//
// iOS native shell: this web <audio> path is SKIPPED entirely (isTauriIOS guards
// below). On the Tauri iOS shell the FCM/APNs notification is the SINGLE sound
// source — foreground via the push plugin's willPresent ([.banner,.sound,.badge]),
// background via APNs. A parallel web <audio> there both double-dings AND, worse,
// registers the WKWebView with iOS's now-playing system (the "music" pill at the
// top of the screen). This mirrors v1's mobile adapter exactly (cyborg7-core
// mobile .../notify/platforms/local.ts: "Skip the local path on mobile; let FCM be
// the single source").

import { isTauriIOS } from "./mobile/push.js";

let audioEl: HTMLAudioElement | null = null;
let unlocked = false;

function element(): HTMLAudioElement | null {
  if (typeof window === "undefined") return null;
  if (!audioEl) {
    audioEl = new Audio("/sounds/notification.mp3");
    audioEl.volume = 0.5;
  }
  return audioEl;
}

// Play the element silently once on the first user gesture so every later
// playNotificationSound() succeeds without the autoplay prompt. Idempotent.
export function unlockNotificationAudio(): void {
  // iOS: never touch the <audio> element — even the silent unlock play()
  // registers the WKWebView with the now-playing system. FCM owns mobile sound.
  if (isTauriIOS()) return;
  if (unlocked) return;
  const a = element();
  if (!a) return;
  unlocked = true;
  const vol = a.volume;
  a.volume = 0;
  a.play()
    .then(() => {
      a.pause();
      a.currentTime = 0;
      a.volume = vol;
      return undefined;
    })
    .catch(() => {
      // No interaction yet — the next gesture will retry.
    });
}

export function playNotificationSound(): void {
  // iOS: FCM/native is the single notification sound source (see header) — the
  // web <audio> here would double-ding and trigger the now-playing pill.
  if (isTauriIOS()) return;
  const a = element();
  if (!a) return;
  a.currentTime = 0;
  a.play().catch(() => {
    // Expected before the first unlock; harmless.
  });
}
